import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Modal, ToggleSwitch } from 'flowbite-react';
import { Change, Quote, User } from '../../models';
import QuotesApi from '../../services/Quotes';
import { Button, EmailSuggestField, Icon, MultiSelect } from '..';
import {
    defaultQuoteSendRecipients,
    quoteRecipientOptions,
} from '../../utils/quoteSendRecipients';
import { roleBadgeColor, roleLabelKey } from '../../utils/clientContacts';
import { buildRecipientEmailSources } from '../../utils/recipientEmailSources';
import { formatPrice } from '../../locales';
import { RootState, useAppDispatch, useAppSelector, loadUsers, loadGroups, loadClients } from '../../redux';

export type QuoteSendModalProps = {
    show: boolean;
    quote: Quote;
    onClose: () => void;
    onSent: (quote: Quote) => void;
};

function formatSenderSignature(user?: { firstname?: string; lastname?: string } | null) {
    const firstname = String(user?.firstname || '').trim();
    const lastname = String(user?.lastname || '').trim().toUpperCase();
    const name = [firstname, lastname].filter(Boolean).join(' ');
    return name ? `${name}.` : '';
}

function uniqueEmails(list: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    list.forEach((email) => {
        const trimmed = String(email || '').trim();
        const key = trimmed.toLowerCase();
        if (!trimmed || seen.has(key))
            return;
        seen.add(key);
        out.push(trimmed);
    });
    return out;
}

function QuoteSendModal(props: QuoteSendModalProps) {
    const { show, quote, onClose, onSent } = props;
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { users } = useAppSelector((state: RootState) => state.users);
    const { groups } = useAppSelector((state: RootState) => state.groups);
    const { clients } = useAppSelector((state: RootState) => state.clients);

    const trackedChange = (quote.trackedChange && quote.trackedChange._id)
        ? quote.trackedChange as Change
        : null;

    const options = useMemo(() => quoteRecipientOptions(quote.client), [quote.client]);
    const readOnly = !quote.isOpenForClientDecision();
    const hasRemovable = (quote.items || []).some((item) => !!item.removable && !item.quantityMode);
    const hasQuantityEditable = (quote.items || []).some((item) => {
        if (!item.quantityMode)
            return false;
        const min = Number(item.quantityMin);
        const max = Number(item.quantityMax);
        return Number.isFinite(min) && Number.isFinite(max) && min < max;
    });
    const projectName = quote.project?.name || '';
    const senderSignature = formatSenderSignature(user);
    const ctaLabel = readOnly ? 'Voir le devis' : 'Voir et accepter le devis';

    const [toEmails, setToEmails] = useState<string[]>([]);
    const [ccEmails, setCcEmails] = useState<string[]>([]);
    const [toInput, setToInput] = useState('');
    const [ccInput, setCcInput] = useState('');
    const [signalUsers, setSignalUsers] = useState<string[]>([]);
    const [signalGroups, setSignalGroups] = useState<string[]>([]);
    const [reminder, setReminder] = useState(false);
    const [subject, setSubject] = useState(quote.defaultSendSubject(false));
    const [personalMessage, setPersonalMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show)
            return;
        dispatch(loadUsers());
        dispatch(loadGroups());
        dispatch(loadClients());
        const defaults = defaultQuoteSendRecipients(quote.client, trackedChange);
        setToEmails(defaults.to);
        setCcEmails(defaults.cc);
        setSignalUsers(defaults.signalUsers);
        setSignalGroups(defaults.signalGroups);
        setToInput('');
        setCcInput('');
        setReminder(false);
        setSubject(quote.defaultSendSubject(false));
        setPersonalMessage('');
        setError('');
        setSending(false);
    }, [show, quote, trackedChange, dispatch]);

    const emailSources = useMemo(() => buildRecipientEmailSources({
        users,
        clients,
        changes: trackedChange ? [trackedChange] : [],
        extraEmails: [...toEmails, ...ccEmails, ...options.map((o) => o.email)],
    }), [users, clients, trackedChange, toEmails, ccEmails, options]);

    const memberOptions = useMemo(
        () => (users || [])
            .filter((member: User) => member.account_type !== 'bot')
            .map((member: User) => ({ value: member._id, label: member.fullname() })),
        [users]
    );
    const groupOptions = useMemo(
        () => (groups || []).map((group: AnyObj) => ({ value: group._id, label: group.name })),
        [groups]
    );

    const onToggleReminder = (checked: boolean) => {
        setReminder(checked);
        const base = `Devis ${quote.slug} — ${quote.name}`;
        const reminderSubject = `Relance — ${base}`;
        if (checked && (subject === base || !subject.trim()))
            setSubject(reminderSubject);
        else if (!checked && subject === reminderSubject)
            setSubject(base);
    };

    const addEmail = (section: 'to' | 'cc', email: string) => {
        const trimmed = String(email || '').trim();
        if (!trimmed)
            return;
        if (section === 'to')
            setToEmails((prev) => uniqueEmails([...prev, trimmed]));
        else
            setCcEmails((prev) => uniqueEmails([...prev, trimmed]));
    };

    const removeEmail = (section: 'to' | 'cc', email: string) => {
        const key = email.toLowerCase();
        if (section === 'to')
            setToEmails((prev) => prev.filter((item) => item.toLowerCase() !== key));
        else
            setCcEmails((prev) => prev.filter((item) => item.toLowerCase() !== key));
    };

    const submit = async () => {
        if (!toEmails.length) {
            setError(String(t('quotes.send_missing_to') || ''));
            return;
        }

        setSending(true);
        setError('');
        try {
            const json = await QuotesApi.send(quote._id, {
                to: toEmails,
                cc: ccEmails,
                subject,
                personalMessage,
                reminder,
                signalUsers: trackedChange ? signalUsers : undefined,
                signalGroups: trackedChange ? signalGroups : undefined,
                trackedChange: trackedChange?._id || null,
            });
            onSent(json.quote);
            onClose();
        } catch (err: any) {
            setError(err?.message || String(t('quotes.send_failed') || ''));
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal show={show} onClose={onClose} size="2xl">
            <Modal.Header>
                {quote.hasBeenSent() ? t('quotes.resend_email') : t('quotes.send_email')}
            </Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    {trackedChange && (
                        <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40 px-3 py-2 text-sm">
                            {t('quotes.tracked_change_send_hint', {
                                slug: trackedChange.slug,
                                title: trackedChange.localizedTitle(i18n.language) || trackedChange.slug,
                            })}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-1">{t('quotes.send_to')}</label>
                        <EmailSuggestField
                            value={toInput}
                            onChange={setToInput}
                            onCommit={(email) => addEmail('to', email)}
                            sources={emailSources}
                            excludedEmails={toEmails}
                            placeholder={t('changes.reply_add_email_placeholder') || ''}
                            allowMulti
                        />
                        {toEmails.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {toEmails.map((email) => (
                                    <Badge key={email} color="info" className="inline-flex items-center gap-1">
                                        <span>{email}</span>
                                        <button type="button" onClick={() => removeEmail('to', email)} aria-label="remove">
                                            <Icon type="close" size={3} color="inherit" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">{t('quotes.send_cc')}</label>
                        <EmailSuggestField
                            value={ccInput}
                            onChange={setCcInput}
                            onCommit={(email) => addEmail('cc', email)}
                            sources={emailSources}
                            excludedEmails={ccEmails}
                            placeholder={t('changes.reply_add_email_placeholder') || ''}
                            allowMulti
                        />
                        {ccEmails.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {ccEmails.map((email) => (
                                    <Badge key={email} color="gray" className="inline-flex items-center gap-1">
                                        <span>{email}</span>
                                        <button type="button" onClick={() => removeEmail('cc', email)} aria-label="remove">
                                            <Icon type="close" size={3} color="inherit" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                    {options.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {options.map((option) => (
                                <button
                                    key={option.email}
                                    type="button"
                                    className="inline-flex items-center gap-1 text-xs border rounded-full px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    onClick={() => addEmail('to', option.email)}
                                >
                                    <span>{option.label}</span>
                                    {option.role && (
                                        <Badge color={roleBadgeColor(option.role)} size="xs">
                                            {t(roleLabelKey(option.role)) || option.role}
                                        </Badge>
                                    )}
                                    {option.isPrimary && (
                                        <Badge color="gray" size="xs">{t('quotes.send_primary')}</Badge>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {trackedChange && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('quotes.send_signal_users')}</label>
                                <MultiSelect
                                    key={`signal-users-${show}-${trackedChange._id}`}
                                    options={memberOptions}
                                    defaultValue={signalUsers}
                                    onChange={(value) => setSignalUsers(value as string[])}
                                    placeholder={t('changes.reply_signal_users') || ''}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('quotes.send_signal_groups')}</label>
                                <MultiSelect
                                    key={`signal-groups-${show}-${trackedChange._id}`}
                                    options={groupOptions}
                                    defaultValue={signalGroups}
                                    onChange={(value) => setSignalGroups(value as string[])}
                                    placeholder={t('changes.reply_signal_groups') || ''}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 dark:border-gray-600 px-3 py-2">
                        <div>
                            <div className="text-sm font-medium">{t('quotes.send_reminder')}</div>
                            <div className="text-xs text-gray-500">{t('quotes.send_reminder_help')}</div>
                        </div>
                        <ToggleSwitch label="" checked={reminder} onChange={onToggleReminder} />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">{t('quotes.send_subject')}</label>
                        <input
                            type="text"
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">{t('quotes.send_preview')}</label>
                        <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-4 space-y-3 text-sm text-gray-800 dark:text-gray-100">
                            <p>Bonjour,</p>
                            <p>
                                Veuillez trouver ci-joint le devis <b>{quote.name}</b> (n° <b>{quote.slug}</b>)
                                {projectName ? <> pour le projet <b>{projectName}</b></> : null}
                                .
                            </p>
                            <p>
                                Montant total : <b>{formatPrice(quote.price || 0)}</b>
                            </p>
                            {hasRemovable && !readOnly && (
                                <p>
                                    Certaines lignes de ce devis sont optionnelles et peuvent être retirées avant acceptation.
                                </p>
                            )}
                            {hasQuantityEditable && !readOnly && (
                                <p>
                                    Certaines lignes permettent d&apos;ajuster la quantité avant acceptation.
                                </p>
                            )}

                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                    {t('quotes.send_message')}
                                </label>
                                <textarea
                                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800"
                                    rows={3}
                                    value={personalMessage}
                                    onChange={(e) => setPersonalMessage(e.target.value)}
                                    placeholder={t('quotes.send_message_placeholder') || ''}
                                />
                            </div>

                            <div>
                                <span className="inline-block px-4 py-2 rounded-md bg-blue-600 text-white font-semibold pointer-events-none select-none">
                                    {ctaLabel}
                                </span>
                                <p className="mt-1 text-xs text-gray-500">{t('quotes.send_cta_readonly')}</p>
                            </div>

                            <p>Le PDF du devis est également joint à cet e-mail.</p>
                            <div className="space-y-0">
                                <p>Cordialement,</p>
                                {senderSignature && <p>{senderSignature}</p>}
                            </div>
                        </div>
                    </div>

                    <p className="text-sm text-gray-500">
                        {t('quotes.send_pdf_note')}
                    </p>
                    {error && (
                        <p className="text-sm text-red-600">{error}</p>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button title={t('common.cancel')} color="secondary" onClick={onClose} />
                <Button
                    title={quote.hasBeenSent() ? t('quotes.resend_email') : t('quotes.send_email')}
                    color="primary"
                    onClick={submit}
                    loading={sending}
                />
            </Modal.Footer>
        </Modal>
    );
}

export default QuoteSendModal;
